import {
	Component,
	type OnChanges,
	type SimpleChanges,
	inject,
	input,
	output,
	signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { type Invoice } from '../../../models/invoice';

import { FloatLabel } from 'primeng/floatlabel';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { type EFilingFilters } from '../../../models/e-filing-filters';
import { type EFilingMenuItem } from '../../../models/e-filing-action-buttons';
import { Toolbar } from 'primeng/toolbar';
import { Menu } from 'primeng/menu';
import { type EmbarkColorKey, embarkColors } from '../../../colors';
import { PrimeIcons, PrimeTemplate } from 'primeng/api';
import { Ripple } from 'primeng/ripple';
import { Tooltip } from 'primeng/tooltip';
import { ParentCompanyStore } from '../../../stores/parent-company-store';
import { type ParentCompany } from '../../../models/parent-company';
import { EmbarkSpinnerComponent } from '../../embark-spinner/embark-spinner.component';

@Component({
	selector: 'app-e-filing-filters',
	imports: [
		FormsModule,
		FloatLabel,
		Select,
		MultiSelect,
		DatePicker,
		Button,
		InputText,
		Toolbar,
		Menu,
		PrimeTemplate,
		Ripple,
		Tooltip,
		EmbarkSpinnerComponent
	],
	templateUrl: './e-filing-filters.component.html',
	styleUrl: './e-filing-filters.component.sass'
})
export class EFilingFiltersComponent implements OnChanges {
	public readonly invoices = input<Invoice[]>([]); //list of invoices from the dashboard
	public readonly isLoading = input<boolean>(false);
	public readonly refreshLoading = input<boolean>(false);
	public readonly filtersApplied = output<EFilingFilters>(); //emits event once filters have been changed/updated
	public readonly exportTable = output<boolean>();
	public readonly unselectAll = output();
	public readonly refreshInvoices = output();
	public readonly submitInvoices = output();

	public clientFilter: { label: string; value: string } | null = null;
	public projectFilter: { label: string; value: string } | null = null;
	public requisitionerFilter: { label: string; value: string } | null = null;
	public nsStatusFilter: { label: string; value: string } | null = null;
	public parentCompany: ParentCompany | null = null;

	public selectedOtStatus: { label: string; value: string } = {
		label: 'All',
		value: 'all'
	};

	public selectedOiStatus: { label: string; value: string } = {
		label: 'All',
		value: 'all'
	};
	public assignedToFilter: { label: string; value: string } | null = null;

	public searchFilter = '';
	public startDateFilter = '';
	public endDateFilter = '';

	public clientOptions: { label: string; value: string | null }[] = [];
	public requisitionerOptions: { label: string; value: string | null }[] = [];
	public nsStatusOptions: { label: string; value: string | null }[] = [];
	public otStatusOptions: { label: string; value: string }[] = [
		{ label: 'All', value: 'all' },
		{ label: 'Approved', value: 'approved' },
		{ label: 'Submitted', value: 'submitted' },
		{ label: 'Saved', value: 'saved' },
		{ label: 'Disputed', value: 'disputed' },
		{ label: 'Cancelled', value: 'cancelled' },
		{ label: 'No Status', value: '' }
	];
	public oiStatusOptions: { label: string; value: string }[] = [
		{ label: 'All', value: 'all' },
		{ label: 'Paid', value: 'paid' },
		{ label: 'Approved', value: 'approved' },
		{ label: 'Submitted', value: 'submitted' },
		{ label: 'Saved', value: 'saved' },
		{ label: 'Stalled', value: 'stalled' },
		{ label: 'Disputed', value: 'disputed' },
		{ label: 'Cancelled', value: 'cancelled' },
		{ label: 'No Status', value: '' }
	];
	public assignedToOptions: { label: string; value: string | null }[] = [];
	public selectedErrorStatuses: string[] = [];
	public errorStatusOptions: { label: string; value: string }[] = [
		{ label: 'Errors', value: 'errors' },
		{ label: 'Warnings', value: 'warnings' },
		{ label: 'None', value: 'none' }
	];

	public readonly embarkColors = embarkColors;

	private readonly parentCompanyStore = inject(ParentCompanyStore);

	public get parentCompanyOptions(): ParentCompany[] {
		return this.parentCompanyStore.parentCompanies();
	}

	public exportButtons = signal<EFilingMenuItem[]>([
		{
			id: 'invoices',
			label: 'Export Invoices',
			bgColor: this.getColor('green75'),
			icon: PrimeIcons.DOWNLOAD,
			command: () => {
				this.exportToExcel();
			}
		},
		{
			id: 'requisitioners',
			label: 'Export Requisitioners',
			bgColor: this.getColor('green50'),
			icon: PrimeIcons.DOWNLOAD,
			command: () => {
				this.exportToExcel(false);
			}
		}
	]);

	public ngOnChanges(changes: SimpleChanges): void {
		//checks for changes across the different filters - adds an "All" button to applicable filters
		if (changes['invoices'] && this.invoices().length > 0) {
			this.nsStatusOptions = [
				{ label: 'All', value: '' },
				...[
					...new Set(
						this.invoices().map((invoice) => invoice.nsStatus)
					)
				].map((nsStatus) => ({ label: nsStatus, value: nsStatus }))
			];

			this.clientOptions = [
				...[
					...new Set(this.invoices().map((invoice) => invoice.name))
				].map((client) => ({ label: client, value: client }))
			];

			this.requisitionerOptions = [
				{ label: 'All', value: '' },
				...[
					...new Set(
						this.invoices().map(({ requisitioner }) => {
							if (!requisitioner) return requisitioner;

							const textarea = document.createElement('textarea');
							textarea.innerHTML = requisitioner;
							return textarea.value;
						})
					)
				].map((requisitioner) => ({
					label: requisitioner,
					value: requisitioner
				}))
			];

			this.assignedToOptions = [
				{ label: 'All', value: '' },
				...[
					...new Set(
						this.invoices().map(
							(invoice) => invoice.assignedTo ?? ''
						)
					)
				].map((assignedTo) => ({
					label: assignedTo,
					value: assignedTo
				}))
			];
		}
	}

	public applyFilters(): void {
		const filters = {
			parentCompany: this.parentCompany?.name ?? '',
			client: this.clientFilter?.value ?? '',
			project: this.projectFilter?.value ?? '',
			search: this.searchFilter.toLowerCase(),
			requisitioner: this.requisitionerFilter?.value ?? '',
			assignedTo: this.assignedToFilter?.value ?? '',
			nsStatus: this.nsStatusFilter?.value ?? '',
			otStatus: this.selectedOtStatus.value,
			oiStatus: this.selectedOiStatus.value,
			startDate: this.startDateFilter,
			endDate: this.endDateFilter,
			errorStatus: this.selectedErrorStatuses
		};

		this.filtersApplied.emit(filters);
	}

	// method that clears/resets process search filters
	public clearFilters(): void {
		this.clientFilter = null;
		this.projectFilter = null;
		this.requisitionerFilter = null;
		this.nsStatusFilter = null;
		this.startDateFilter = '';
		this.endDateFilter = '';
		this.searchFilter = '';
		this.assignedToFilter = null;
		this.selectedOtStatus = { label: 'All', value: 'all' };
		this.selectedOiStatus = { label: 'All', value: 'all' };
		this.selectedErrorStatuses = [];
		this.applyFilters();
	}

	public selectedCount(): number {
		return this.invoices().filter(({ selected }) => selected).length;
	}

	public unselectInvoices(): void {
		this.unselectAll.emit();
	}

	public refresh(): void {
		this.refreshInvoices.emit();
	}

	public exportToExcel(invoices = true): void {
		this.exportTable.emit(invoices);
	}

	private getColor(key: EmbarkColorKey): string {
		return this.embarkColors[key];
	}
}
